import { CsvFormatConfiguration, ExportedCsv, ValueFormatter } from '../../models';
import { ILoggerService } from '../../services/general';

export interface IFormatCsvService<T> {
    getFormattedCsv(items: T[], configuration?: CsvFormatConfiguration): string[][];
}

// By default, all first level properties of object are included
// exclusion list (CsvFormatConfiguration > excludedFields) list all first level properties to exclude
// All referenced sub-properties are also included
// Convention: To access object sub-properties, use underscore in property name
// For example, if you want to include item.parameters.low, you should use 'parameters_low' as property name.
// Default usage to format to csv all field of an object:
// getFormattedCsv(items);
export class FormatCsvService<T> implements IFormatCsvService<T> {
    static serviceId = 'formatCsvService';

    static $inject = [];

    // Get formated csv
    getFormattedCsv(items: T[], configuration?: CsvFormatConfiguration): string[][] {
        const results: ExportedCsv = { headers: [], content: [] };
        const properties = this.getProperties(items, configuration);
        const flattenedItems = this.flattenItems(items, properties, configuration);
        if (configuration?.fields) {
            results.headers = this.getHeaders(flattenedItems, configuration, properties);
            results.content = this.getContent(flattenedItems, configuration, properties);
        }
        return [results.headers].concat(results.content);
    }

    // Get list of properties (corresponding to flattened item structure) to export
    private getProperties(items: T[], configuration: CsvFormatConfiguration): string[] {
        let properties: string[] = [];
        // Add all first level properties from configuration not excluded
        if (items?.length > 0 && configuration?.fields && Object.keys(configuration.fields).length > 0) {
            items.forEach(item => {
                const configurationProperties = Object.keys(configuration.fields)
                    .filter(property => !properties.includes(property)
                        && this.isPropertyExist(item, property));
                properties = properties.concat(configurationProperties);
            });
            if (properties.length < Object.keys(configuration.fields).length) {
                const leftoverConfigurationProperties = Object.keys(configuration.fields)
                    .filter(property => !properties.includes(property));
                console.warn(`Following properties aren't available in data: ${leftoverConfigurationProperties.join(', ')}.`);
                properties = properties.concat(leftoverConfigurationProperties);
            }
        }
        // Add all first level remaining properties from item not excluded
        if (items?.length > 0) {
            items.forEach(item => {
                const remainingProperties = Object.keys(item)
                    .filter(property => !properties.includes(property)
                        && !configuration?.excludedFields?.includes(property));
                properties = properties.concat(remainingProperties);
            });
        }
        return properties;
    }

    // Flatten items
    private flattenItems(items: T[], properties: string[], configuration?: CsvFormatConfiguration): T[] {
        const flattenedItems = [];
        items.forEach(item => {
            const flattenedItem = {};
            properties.forEach(property => {
                flattenedItem[property] = this.getValue(item, property, configuration);
            });
            flattenedItems.push(flattenedItem);
        });
        return flattenedItems;
    }

    // Get CSV headers
    private getHeaders(items: T[], configuration: CsvFormatConfiguration, properties: string[]): string[] {
        const headers: string[] = [];
        if (items?.length > 0) {
            const item = items[0];
            properties.forEach(property => {
                if (this.isIncluded(property, item, configuration)) {
                    if (property in configuration?.fields && configuration?.fields[property].headerText) {
                        headers.push(configuration.fields[property].headerText);
                    } else {
                        headers.push(property);
                    }
                }
            });
        }
        return headers;
    }

    // Get CSV content
    private getContent(items: T[], configuration: CsvFormatConfiguration, properties: string[]): string[][] {
        const content: string[][] = [];
        items.forEach((item, index) => {
            const values: string[] = [];
            properties.forEach(property => {
                if (this.isIncluded(property, item, configuration)) {
                    this.setFlattenedValue(values, item, property, configuration, index);
                }
            });
            content.push(values);
        });
        return content;
    }

    // Check if a property exist
    private isPropertyExist(item: T, property: string): boolean {
        const splitKey = property.split('_');
        let isKeyExist = (splitKey?.length === 1) && (property in item);
        if (splitKey?.length > 1) {
            let current = item;
            for (let i = 0; i < splitKey.length; i++) {
                const keyPart = splitKey[i];
                isKeyExist = current && (keyPart in current);
                current = isKeyExist
                    ? current[keyPart]
                    : undefined;
            }
        }
        return isKeyExist;
    }

    // Get and format value handling sub-properties
    private getValue(item: T, property: string, configuration: CsvFormatConfiguration) {
        let formatter: ValueFormatter<any>;
        const splitKey = property.split('_');
        let current: any = item;
        if (splitKey.length === 1) {
            current = item[property];
        } else {
            for (let i = 0; i < splitKey.length; i++) {
                const keyPart = splitKey[i];
                current = current && current[keyPart]
                    ? current[keyPart]
                    : undefined;
                if (current === undefined) {
                    break;
                }
            }
        }
        if (configuration && property in configuration?.fields) {
            formatter = configuration.fields[property]?.formatter;
        }
        if (current && formatter) {
            current = formatter(current);
        }
        return current;
    }

    // Get flattened value (or custom value if defined)
    private setFlattenedValue(values: string[], item: T, property: string, configuration: CsvFormatConfiguration, index: number) {
        let value = item[property];
        if (configuration
            && property in configuration.fields
            && configuration.fields[property].customValues?.length > 0) {
            value = configuration?.fields[property].customValues[index];
        }
        values.push(value);
    }

    // Chek if property has to be included in csv format
    private isIncluded(property: string, item: T, configuration: CsvFormatConfiguration): boolean {
        return property
            && (property in item)
            && !configuration?.excludedFields?.includes(property);
    }
}